import {
    Anchor,
    Badge,
    Button,
    Center,
    ColumnDef,
    createColumnHelper,
    EmptyState,
    getFacetedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    Paper,
    Table,
    TableProps,
    useTable,
} from '@components/mantine';
import {fileOwnership, Framework, type OwnedFile, teamAliasMappings} from '@core/debug';
import {Fragment, FunctionComponent, ReactNode, useMemo} from 'react';

import classes from './FileOwnershipTable.module.css';
import {FrameworkPredicate} from './FrameworkPredicate';
import {OwnerPredicate} from './OwnerPredicate';

const columnHelper = createColumnHelper<OwnedFile>();

const FrameworkBadgeMapping: Record<Framework, ReactNode> = {
    Backbone: <Badge.Critical>Backbone</Badge.Critical>,
    PlasmaReact: <Badge.Warning>Plasma React</Badge.Warning>,
    Mantine: <Badge.Primary>Mantine</Badge.Primary>,
};

const columns: Array<ColumnDef<OwnedFile>> = [
    columnHelper.accessor('frontendOwner', {
        header: 'Frontend Owner',
    }),
    columnHelper.accessor('backendOwner', {
        header: 'Backend Owner',
        enableSorting: false,
        cell: ({getValue, row: {original}}) => {
            const value = getValue();
            return <Center>{value === original.frontendOwner ? '-' : value}</Center>;
        },
    }),
    columnHelper.accessor('framework', {
        header: 'Framework',
        enableSorting: false,
        cell: ({getValue}) => {
            const value = getValue();
            const frameworks = Array.isArray(value) ? value : [value];
            return frameworks.map((framework) => (
                <Fragment key={framework}>{FrameworkBadgeMapping[framework]}</Fragment>
            ));
        },
    }),
    columnHelper.accessor('filePath', {
        header: 'File',
        cell: ({getValue, row}) => (
            <Anchor
                href={`https://github.com/coveo-platform/admin-ui/tree/master/${getValue()}#L${row.original.lineNumber}`}
            >
                {getValue()}
            </Anchor>
        ),
    }),
    columnHelper.accessor('component', {
        header: 'Component',
        cell: ({row}) => `${row.original.parent} > ${row.original.component}`,
    }),
];

const options: TableProps<OwnedFile>['options'] = {
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: 'auto',
};

export const FileOwnershipTable: FunctionComponent = () => {
    const table = useTable<OwnedFile>({
        initialState: {
            predicates: {backendOwner: '', framework: '', frontendOwner: ''},
            pagination: {perPage: 10, page: 0},
            sorting: [{id: 'frontendOwner', desc: false}],
        },
    });

    const resolvedOwnership = useMemo(
        () =>
            fileOwnership.map((file) => ({
                ...file,
                backendOwner: teamAliasMappings[file.backendOwner] ?? file.backendOwner,
                frontendOwner: teamAliasMappings[file.frontendOwner] ?? file.frontendOwner,
            })),
        [],
    );

    const backendOwners = useMemo(
        () =>
            Array.from(
                new Set(
                    resolvedOwnership
                        .filter(({backendOwner, frontendOwner}) => backendOwner !== frontendOwner)
                        .map(({backendOwner}) => backendOwner),
                ),
            ).sort(),
        [resolvedOwnership],
    );
    const frontendOwners = useMemo(
        () => Array.from(new Set(resolvedOwnership.map(({frontendOwner}) => frontendOwner))).sort(),
        [resolvedOwnership],
    );
    const frameworks = useMemo(
        () => Array.from(new Set(resolvedOwnership.flatMap(({framework}) => framework))).sort(),
        [resolvedOwnership],
    );

    // manually apply predicates
    const data = useMemo(
        () =>
            resolvedOwnership
                .filter(
                    ({framework}) =>
                        table.state.predicates.framework === '' ||
                        table.state.predicates.framework === framework ||
                        (Array.isArray(framework) && framework.includes(table.state.predicates.framework as Framework)),
                )
                .filter(
                    ({frontendOwner}) =>
                        table.state.predicates.frontendOwner === '' ||
                        table.state.predicates.frontendOwner === frontendOwner,
                )
                .filter(
                    ({backendOwner}) =>
                        table.state.predicates.backendOwner === '' ||
                        table.state.predicates.backendOwner === backendOwner,
                ),
        [resolvedOwnership, table.state.predicates],
    );

    return (
        <Paper radius="md" withBorder className={classes.container}>
            <Table<OwnedFile>
                store={table}
                data={data}
                columns={columns}
                options={options}
                getRowId={({filePath, lineNumber}) => `${filePath}:${lineNumber}`}
            >
                <Table.Header>
                    <FrameworkPredicate
                        frameworks={frameworks}
                        classNames={{
                            predicate: classes.predicateRoot,
                            predicateWrapper: classes.predicateWrapper,
                            predicateSelect: classes.predicateSelect,
                        }}
                    />
                    <OwnerPredicate<'backendOwner'>
                        id="backendOwner"
                        label="Backend Owner"
                        owners={backendOwners}
                        classNames={{
                            predicate: classes.predicateRoot,
                            predicateWrapper: classes.predicateWrapper,
                            predicateSelect: classes.predicateSelect,
                        }}
                    />
                    <OwnerPredicate<'frontendOwner'>
                        id="frontendOwner"
                        label="Frontend Owner"
                        owners={frontendOwners}
                        classNames={{
                            predicate: classes.predicateRoot,
                            predicateWrapper: classes.predicateWrapper,
                            predicateSelect: classes.predicateSelect,
                        }}
                    />
                    <Table.Filter
                        placeholder="Search"
                        classNames={{filterRoot: classes.filterRoot, filterWrapper: classes.filterWrapper}}
                    />
                </Table.Header>
                <Table.Footer>
                    <Table.PerPage values={[10, 25, 100]} />
                    <Table.Pagination />
                </Table.Footer>
                <Table.NoData>
                    <EmptyState>
                        <EmptyState.Title order={4}>Nothing found for "{table.state.globalFilter}"</EmptyState.Title>
                        <Button.Primary onClick={table.clearFilters}>Clear filter</Button.Primary>
                    </EmptyState>
                </Table.NoData>
            </Table>
        </Paper>
    );
};
